import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Breadcrumb, Card, Form, Image } from 'react-bootstrap';
import { MDBTable, MDBTableHead, MDBTableBody } from 'mdb-react-ui-kit';
import { Button } from 'primereact/button';
import '../../assets/css/calender.css';
import axios from 'axios';
import config from '../../config';
import CustomToast from '../../components/CustomToast';
import { useAuth } from "../../context/AuthContext";
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';

const Addholiday = () => {
    const [date, setDate] = useState(null);
    const [holidayName, setHolidayName] = useState('');
    const [holidays, setHolidays] = useState([]);
    const [editingHoliday, setEditingHoliday] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [image, setImage] = useState(null); // 🖼️ New state for image file
    const [preview, setPreview] = useState(null); // 🖼️ Image preview
    const toastRef = useRef(null);
    const { accessToken } = useAuth();

    useEffect(() => {
        fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.apiBASEURL}/HolidayRoutes/holidays`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setHolidays(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching holidays', error);
            setError('Error fetching holidays. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (severity, detail) => {
        const summary = severity.charAt(0).toUpperCase() + severity.slice(1);
        toastRef.current.show(severity, summary, detail);
    };

    const formatDateToUTC = (date) => {
        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        return utcDate.toISOString().split('T')[0];
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setImage(file);
        if (file) {
            const previewURL = URL.createObjectURL(file);
            setPreview(previewURL);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!holidayName || !date || !status) {
            setError('Please fill in all required fields.');
            showMessage('error', 'Please fill in all required fields.');
            return;
        }

        const formData = new FormData();
        formData.append('holiday_name', holidayName);
        formData.append('holiday_date', formatDateToUTC(date));
        formData.append('status', status);
        if (image) formData.append('image', image);

        setLoading(true);
        try {
            if (editingHoliday) {
                // 🟡 Update holiday
                await axios.put(`${config.apiBASEURL}/HolidayRoutes/holiday/${editingHoliday.holiday_id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                showMessage('success', 'Holiday updated successfully');
            } else {
                // 🟢 Add new holiday
                if (!image) {
                    showMessage('error', 'Please upload an image.');
                    setLoading(false);
                    return;
                }

                await axios.post(`${config.apiBASEURL}/HolidayRoutes/addholiday`, formData, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                showMessage('success', 'Holiday added successfully');
            }

            fetchHolidays();
            resetForm();
        } catch (error) {
            console.error('Error saving holiday:', error);
            showMessage('error', 'Error saving holiday. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (holiday) => {
        setEditingHoliday(holiday);
        setHolidayName(holiday.holiday_name);
        setDate(new Date(holiday.holiday_date));
        setStatus(holiday.status);
        setPreview(holiday.image_url || null);
        setImage(null);
    };

    const deleteHoliday = async (holidayId) => {
        setLoading(true);
        try {
            await axios.delete(`${config.apiBASEURL}/HolidayRoutes/holiday/${holidayId}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            fetchHolidays();
            showMessage('success', 'Holiday deleted successfully');
        } catch (error) {
            console.error('Error deleting holiday', error);
            showMessage('error', 'Error deleting holiday. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setHolidayName('');
        setDate(null);
        setStatus('');
        setImage(null);
        setPreview(null);
        setEditingHoliday(null);
        setError('');
    };

    return (
        <>
            <Row className='body_content'>
                <Row className='mx-0'>
                    <Col md={6} lg={9} className='mb-4'>
                        <Breadcrumb>
                            <Breadcrumb.Item active>Holiday List</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>

                    <Row className="holidayPage">
                        {/* Left Side Form */}
                        <Col lg='4' md='12' className="pe-lg-3 ps-0">
                            <Card className="h-auto sticky-top">
                                <Card.Header><b>{editingHoliday ? 'Edit Holiday' : 'Add Holidays'}</b></Card.Header>
                                <Card.Body>
                                    <Form onSubmit={handleSubmit}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Holiday Name <span className='text-danger'>*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Holiday Name"
                                                value={holidayName}
                                                onChange={(e) => setHolidayName(e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Holiday Date <span className='text-danger'>*</span></Form.Label>
                                            <br />
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={date ? formatDateToUTC(date) : ''}
                                                onChange={(e) => setDate(new Date(e.target.value))}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Status <span className='text-danger'>*</span></Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                            >
                                                <option value="">Select Status</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </Form.Control>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Image <span className='text-danger'>*</span></Form.Label>
                                            <Form.Control type="file" accept="image/*" onChange={handleImageChange} />
                                            {preview && (
                                                <div className="mt-2 text-center">
                                                    <Image
                                                        src={preview}
                                                        alt="Preview"
                                                        thumbnail
                                                        style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                                                    />
                                                </div>
                                            )}
                                        </Form.Group>

                                        <div className="d-flex justify-content-end">
                                            <Button
                                                label={editingHoliday ? 'Update' : 'Save'}
                                                icon={loading ? 'pi pi-spin pi-spinner' : null}
                                                disabled={loading}
                                                severity="info"
                                                style={{ width: '100px', height: '46px' }}
                                            />
                                        </div>
                                        {error && <p className="text-danger mt-2">{error}</p>}
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Right Side Table */}
                        <Col lg='8' md='12'>
                            <Card className='text-center h-auto'>
                                <MDBTable responsive striped hover small>
                                    <MDBTableHead light>
                                        <tr>
                                            <th>#</th>
                                            <th>Image</th>
                                            <th>Title</th>
                                            <th>Date</th>
                                            
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </MDBTableHead>
                                    <MDBTableBody>
                                        {loading ? (
                                            <tr><td colSpan="6">Loading...</td></tr>
                                        ) : holidays.length === 0 ? (
                                            <tr><td colSpan="6">No holidays found.</td></tr>
                                        ) : (
                                            holidays.map((holiday, index) => (
                                                <tr key={holiday.holiday_id}>
                                                    <td>{index + 1}</td>
                                                     <td>
                                                        {holiday.image_url ? (
                                                            <img
                                                                src={holiday.image_url}
                                                                alt="Holiday"
                                                                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                                                            />
                                                        ) : (
                                                            'No Image'
                                                        )}
                                                    </td>
                                                    <td>{holiday.holiday_name}</td>
                                                    <td>
                                                        {holiday.holiday_date
                                                            ? new Date(holiday.holiday_date).toLocaleDateString('en-GB')
                                                            : ''}
                                                    </td>
                                                    <td>{holiday.status}</td>
                                                    <td>
                                                        <Button
                                                            icon="pi pi-pencil"
                                                            className="p-button-rounded p-button-text p-button-primary"
                                                            onClick={() => handleEdit(holiday)}
                                                        />
                                                        <Button
                                                            icon="pi pi-trash"
                                                            className="p-button-rounded p-button-text p-button-danger"
                                                            onClick={() => deleteHoliday(holiday.holiday_id)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </MDBTableBody>
                                </MDBTable>
                            </Card>
                        </Col>

                        <CustomToast ref={toastRef} />
                    </Row>
                </Row>
            </Row>
        </>
    );
};

export default Addholiday;
